/**
 * Right-hand detail panel: the big glyph, its component breakdown, readings,
 * example vocabulary, and the note / mastered / flashcard actions.
 */

import { useEffect, useRef, useState } from 'react'
import { ApiError, api, levelColor, type KanjiDetail, type Level } from '../api'
import { ErrorNote, LevelBadge, Spinner } from './ui'

interface Props {
  char: string
  signedIn: boolean
  /** The map's level filter, used to mute kanji from other levels. */
  activeLevel?: Level | null
  /**
   * Rendered inside a container that already provides the frame, the title and a
   * close control - the mobile bottom sheet. Drops this component's own card
   * border and header so the two do not stack up.
   */
  embedded?: boolean
  onClose: () => void
  onNavigate: (char: string) => void
  onChanged?: () => void
}

export default function DetailPanel({
  char,
  signedIn,
  activeLevel = null,
  embedded = false,
  onClose,
  onNavigate,
  onChanged,
}: Props) {
  const [data, setData] = useState<KanjiDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [noteOpen, setNoteOpen] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const noteRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    setNoteOpen(false)
    api
      .detail(char)
      .then((d) => {
        if (!alive) return
        setData(d)
        setNoteDraft(d.note ?? '')
      })
      .catch((e) => alive && setError(e instanceof ApiError ? e.message : 'could not load that character'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [char])

  useEffect(() => {
    if (noteOpen) noteRef.current?.focus()
  }, [noteOpen])

  const refresh = async () => {
    const d = await api.detail(char)
    setData(d)
    onChanged?.()
  }

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await refresh()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'that did not work')
    } finally {
      setBusy(false)
    }
  }

  const accent = data?.level ? levelColor[data.level as Level] : '#55607a'
  // Matches the map: anything that is not a kanji of the level in view is muted,
  // component forms included.
  const offLevel =
    activeLevel === null
      ? 0
      : (data?.used_in ?? []).filter((n) => n.level !== activeLevel).length

  return (
    <aside
      className={
        embedded
          ? 'flex w-full flex-col'
          : 'card flex h-full w-full flex-col overflow-hidden animate-pop'
      }
    >
      <header
        className={`flex items-center gap-2 border-b border-ink-700/70 px-4 py-2.5 ${
          embedded ? '' : 'justify-between'
        }`}
      >
        <div className="flex flex-1 items-center gap-2">
          <LevelBadge level={data?.level ?? null} />
          {data?.strokes ? <span className="text-[11px] text-ink-500">{data.strokes} strokes</span> : null}
          {data?.freq ? <span className="text-[11px] text-ink-500">· freq #{data.freq}</span> : null}
        </div>
        {/* The sheet supplies its own close button; a second one here would sit
            directly beneath it. */}
        {!embedded && (
          <button type="button" onClick={onClose} aria-label="Close panel" className="btn-ghost !px-2 !py-1 text-xs">
            ✕
          </button>
        )}
      </header>

      <div className={embedded ? 'px-4 py-4' : 'flex-1 overflow-y-auto px-4 py-4'}>
        {loading && <Spinner label="Loading character…" />}
        {error && <ErrorNote>{error}</ErrorNote>}

        {data && (
          <>
            <div className="flex flex-col items-center">
              <div
                className="jp flex h-28 w-28 items-center justify-center rounded-2xl border text-6xl leading-none"
                style={{ borderColor: `${accent}55`, background: `${accent}12`, color: '#f1f5f9' }}
              >
                {data.char}
              </div>
              <div className="mt-3 w-full rounded-xl px-3 py-1.5 text-center text-sm font-bold"
                   style={{ background: `${accent}22`, color: accent }}>
                {data.meanings.slice(0, 4).join(', ') || 'unnamed component'}
              </div>
            </div>

            {/* component breakdown - the core teaching move */}
            {data.components.length > 0 && (
              <section className="mt-5">
                <h3 className="label">Built from</h3>
                <div className="flex flex-wrap items-stretch gap-2">
                  {data.components.map((c, i) => (
                    <div key={`${c.char}-${c.position}`} className="flex items-center gap-2">
                      {i > 0 && <span className="text-ink-600">+</span>}
                      <button
                        type="button"
                        onClick={() => onNavigate(c.char)}
                        className="flex min-w-[74px] flex-col items-center gap-0.5 rounded-xl border border-ink-700
                                   bg-ink-850 px-2.5 py-2 transition hover:border-ink-600 hover:bg-ink-800"
                        title={`Open ${c.char}`}
                      >
                        <span className="jp text-2xl leading-none text-slate-100">{c.char}</span>
                        <span className="text-[10px] font-semibold text-slate-300">{c.meaning}</span>
                        <span className="text-[10px] text-ink-500">{c.romaji}</span>
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* readings */}
            {(data.on_readings.length > 0 || data.kun_readings.length > 0) && (
              <section className="mt-5 grid grid-cols-2 gap-3">
                <ReadingBlock title="Onyomi" kana={data.on_readings} romaji={data.on_romaji} />
                <ReadingBlock title="Kunyomi" kana={data.kun_readings} romaji={data.kun_romaji} />
              </section>
            )}

            {data.radical_name && (
              <p className="mt-3 text-[11px] text-ink-500">
                Radical name: <span className="jp text-slate-300">{data.radical_kana}</span>{' '}
                <span className="text-slate-400">({data.radical_name})</span>
                {data.radical_no ? ` · Kangxi #${data.radical_no}` : ''}
              </p>
            )}

            {data.examples.length > 0 && (
              <section className="mt-5">
                <h3 className="label">Examples</h3>
                <ol className="grid gap-1.5">
                  {data.examples.map((e, i) => (
                    <li key={e.word} className="flex items-baseline gap-2 rounded-lg bg-ink-850/60 px-2.5 py-1.5">
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-500/20 text-[9px] font-bold text-brand-400">
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="jp truncate text-sm text-slate-100">
                          {e.word} <span className="text-ink-500">【{e.reading}】</span>
                        </div>
                        <div className="truncate text-[11px] text-ink-500">
                          {e.romaji} — {e.meaning}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* the sideways expansion: everything built from this part, across all
                levels. Entries outside the active filter are muted to match the map. */}
            {data.used_in.length > 0 && (
              <section className="mt-5">
                <h3 className="label">
                  Used in {data.used_in.length} kanji
                  {offLevel > 0 && (
                    <span className="ml-1.5 font-normal normal-case tracking-normal text-ink-600">
                      · {offLevel} outside {activeLevel}
                    </span>
                  )}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {data.used_in.map((n) => {
                    const dim = activeLevel !== null && n.level !== activeLevel
                    return (
                      <button
                        key={n.char}
                        type="button"
                        onClick={() => onNavigate(n.char)}
                        title={`${n.meaning} (${n.romaji})${dim ? ` — ${n.level}, outside the current filter` : ''}`}
                        aria-label={`Open ${n.char} — ${n.meaning}`}
                        // These are navigation, not decoration: each one opens
                        // another character. At 36x28 they were the smallest
                        // targets in the app and sat shoulder to shoulder, so a
                        // mistap opened the wrong kanji.
                        className={`jp grid h-11 min-w-[44px] place-items-center rounded-lg border bg-ink-850
                                    px-2 text-lg leading-none transition active:bg-ink-800
                                    hover:border-brand-500/60 hover:text-white hover:opacity-100 ${
                                      dim
                                        ? 'border-dashed border-ink-700 text-ink-500 opacity-60'
                                        : 'border-ink-700 text-slate-200'
                                    }`}
                        style={!dim && n.level ? { borderColor: `${levelColor[n.level]}44` } : undefined}
                      >
                        {n.char}
                      </button>
                    )
                  })}
                </div>
              </section>
            )}

            {noteOpen && (
              <section className="mt-5">
                <label className="label" htmlFor="note-body">
                  Your note
                </label>
                <textarea
                  id="note-body"
                  ref={noteRef}
                  className="input min-h-[80px] resize-y"
                  value={noteDraft}
                  maxLength={4000}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="A mnemonic that helps you remember this one…"
                />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    className="btn-primary !py-1.5 text-xs"
                    disabled={busy}
                    onClick={() => act(() => api.saveNote(char, noteDraft)).then(() => setNoteOpen(false))}
                  >
                    Save note
                  </button>
                  <button type="button" className="btn-ghost !py-1.5 text-xs" onClick={() => setNoteOpen(false)}>
                    Cancel
                  </button>
                </div>
              </section>
            )}

            {data.note && !noteOpen && (
              <p className="mt-4 rounded-xl border border-ink-700 bg-ink-850/70 px-3 py-2 text-xs text-slate-300">
                {data.note}
              </p>
            )}
          </>
        )}
      </div>

      {data && (
        <footer className="grid grid-cols-3 gap-2 border-t border-ink-700/70 px-4 py-3">
          {signedIn ? (
            <>
              <button
                type="button"
                className="btn bg-rose-400/20 text-rose-200 hover:bg-rose-400/30 !py-2 text-xs"
                onClick={() => setNoteOpen((v) => !v)}
              >
                {data.note ? 'Edit note' : 'Notes'}
              </button>
              <button
                type="button"
                disabled={busy}
                className={`btn !py-2 text-xs ${
                  data.status === 'mastered'
                    ? 'bg-brand-500 text-ink-950 hover:bg-brand-400'
                    : 'bg-brand-500/20 text-brand-400 hover:bg-brand-500/30'
                }`}
                onClick={() => act(() => api.setMastered(char, data.status !== 'mastered'))}
              >
                {data.status === 'mastered' ? '✓ Mastered' : 'Mastered'}
              </button>
              <button
                type="button"
                disabled={busy}
                className={`btn !py-2 text-xs ${
                  data.in_flashcards ? 'bg-n1 text-ink-950' : 'bg-n1/20 text-n1 hover:bg-n1/30'
                }`}
                onClick={() =>
                  act(() => (data.in_flashcards ? api.removeFlashcard(char) : api.addFlashcard(char)))
                }
              >
                {data.in_flashcards ? '✓ Saved' : '+ Flashcard'}
              </button>
            </>
          ) : (
            <p className="col-span-3 text-center text-xs text-ink-500">
              Sign in to track progress, save notes and build flashcards.
            </p>
          )}
        </footer>
      )}
    </aside>
  )
}

function ReadingBlock({ title, kana, romaji }: { title: string; kana: string[]; romaji: string[] }) {
  return (
    <div className="rounded-xl border border-ink-700/70 bg-ink-850/60 px-3 py-2">
      <div className="mb-1 text-center text-[10px] font-bold uppercase tracking-wide text-ink-500">{title}</div>
      {kana.length === 0 ? (
        <div className="text-center text-xs text-ink-600">—</div>
      ) : (
        <ul className="space-y-0.5 text-center">
          {kana.slice(0, 5).map((k, i) => (
            <li key={k}>
              <span className="jp text-sm text-slate-100">{k}</span>
              {romaji[i] && <span className="ml-1.5 text-[10px] text-ink-500">{romaji[i]}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
